import { useEffect, useRef, useState } from 'react'
import { supabase } from '@/integrations/supabase/client'
import type { RoadRecord } from './types'
import type { RouteTrims } from './trim'
import { buildRouteWaypoints } from './ride'

export interface RouteSummary {
  miles: number
  seconds: number | null
}

export interface RouteSummaryState {
  summary: RouteSummary | null
  // True while the connected-route totals for the current route are being
  // fetched/computed. False once they land OR if routing failed (callers fall
  // back to per-road sums in that case instead of showing "computing…" forever).
  computing: boolean
}

// Accurate route totals for the tray: sends the route's full waypoint chain
// to the route-summary Edge Function, which runs one server-side OSRM pass
// over the connected ride (connector roads included) and caches the result.
// Debounced so rapid add/remove/reorder doesn't spam routing requests.
export function useRouteSummary(routeRoads: RoadRecord[], trims?: RouteTrims): RouteSummaryState {
  const [state, setState] = useState<RouteSummaryState>({ summary: null, computing: false })
  const requestToken = useRef(0)

  // Order matters — a reorder produces a different ride, so recompute.
  // Trims change the chain too; JSON gives a stable signature for them.
  const trimSignature = trims ? JSON.stringify(trims) : ''
  const routeSignature = routeRoads.map((r) => r.id).join('>') + trimSignature

  useEffect(() => {
    setState({ summary: null, computing: false })
    if (routeRoads.length < 1) return
    const token = ++requestToken.current
    setState({ summary: null, computing: true })
    const timer = setTimeout(async () => {
      const pts = await buildRouteWaypoints(routeRoads, trims)
      if (pts.length < 2 || requestToken.current !== token) {
        // Not enough geometry to route — per-road sums remain the source of truth.
        if (requestToken.current === token) setState({ summary: null, computing: false })
        return
      }
      try {
        const { data, error } = await supabase.functions.invoke('route-summary', {
          body: { waypoints: pts },
        })
        if (requestToken.current !== token) return
        if (!error && data && typeof data.distance_miles === 'number') {
          setState({
            summary: {
              miles: data.distance_miles,
              seconds: typeof data.duration_seconds === 'number' ? data.duration_seconds : null,
            },
            computing: false,
          })
        } else {
          setState({ summary: null, computing: false })
        }
      } catch {
        // Routing unavailable — the tray keeps its per-road fallback totals.
        if (requestToken.current === token) setState({ summary: null, computing: false })
      }
    }, 700)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSignature]) // trims are captured via routeSignature's trim part

  return state
}
